import { useCallback, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import {
  getEtlJob,
  getEtlJobCatalog,
  getEtlJobTriggerRequest,
  getRecentEtlJobTriggerRequests,
  triggerEtlJob,
} from '../services/etlJobsApi'
import { REQUEST_HISTORY_LIMIT, followUpToast, triggerRequestsPollInterval } from '../components/jobs/requestFormat'
import { signInRequiredText } from '../components/jobs/haltClearFormat'
import { errorKind } from '../utils/format'
import type { EtlJob, EtlJobTriggerRequest } from '../types/etlJobs'

/**
 * The job catalogue.
 *
 * Refetched rarely: it only changes when Automaton restarts. A stale `publishedAt` is itself the useful
 * signal — it means the worker has not restarted since a job was added.
 *
 * KEEP the signature and the key: useLighthouseAdmin reads the same query, so the rail and this page share
 * one request.
 */
export function useEtlJobCatalog() {
  return useQuery({
    queryKey: ['etl-job-catalog'],
    queryFn: getEtlJobCatalog,
    staleTime: 60_000,
  })
}

/**
 * One job by id, for a deep link the catalogue cannot answer — it failed to load, or does not list the id.
 *
 * Only fetched when `enabled`. A 404 is an answer ("no such job is registered"), not a fault, so it is not
 * retried; anything else gets React Query's usual retries.
 */
export function useEtlJob(jobId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['etl-job', jobId],
    queryFn: () => getEtlJob(jobId as string),
    enabled: enabled && jobId !== null,
    staleTime: 60_000,
    retry: (failureCount, error) => errorKind(error) !== 'not-found' && failureCount < 3,
  })
}

/**
 * Recent trigger requests — everyone's, newest first — polled while any is still unresolved.
 *
 * "Unresolved" is wider than Requested: the run a request causes is written only once its Hangfire job
 * starts, after the request already reads Enqueued, so polling stays on (slower) until that run is linked
 * or ten minutes have passed. See triggerRequestsPollInterval.
 */
export function useEtlJobTriggerRequests(limit = REQUEST_HISTORY_LIMIT) {
  return useQuery({
    queryKey: ['etl-job-trigger-requests', limit],
    queryFn: () => getRecentEtlJobTriggerRequests(limit),
    refetchInterval: (query) => triggerRequestsPollInterval(query.state.data, Date.now()),
  })
}

/** How many times, three seconds apart, the form re-reads a request before giving up on following it. */
const FOLLOW_ATTEMPTS = 20

/**
 * Triggers a job and follows the request until Automaton has acted on it.
 *
 * Two stages on purpose. Accepting a trigger only means the intent is recorded — the API cannot enqueue
 * Hangfire work itself — so the UI must not claim a run has started. Even `Enqueued` only means Hangfire
 * holds it: the toast says "queued", never "started".
 */
/**
 * @param entraConfigured Whether this build can sign anyone in with Entra (the page passes `isEntraConfigured()`;
 *   this module does not import src/auth, which reads `window` at load). Words the sign-in refusal toast.
 */
export function useTriggerEtlJob(entraConfigured = true) {
  const queryClient = useQueryClient()
  const [request, setRequest] = useState<EtlJobTriggerRequest | null>(null)
  /** True once following stopped while the request still read Requested (or could not be read). */
  const [followTimedOut, setFollowTimedOut] = useState(false)
  /**
   * The request the form is showing. Following outlives the dialog — close it and open another job, and
   * the first request is still being read — so a late read only lands if it is still the one on screen.
   */
  const shownRequestId = useRef<string | null>(null)

  /**
   * Re-reads the request until it leaves Requested, for roughly a minute. Returns the last state read, or
   * null when no read succeeded — the caller says which, instead of going quiet.
   */
  const followUntilResolved = useCallback(async (requestId: string) => {
    let latest: EtlJobTriggerRequest | null = null

    for (let attempt = 0; attempt < FOLLOW_ATTEMPTS; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, 3_000))

      const read = await getEtlJobTriggerRequest(requestId).catch(() => null)
      if (!read) continue

      latest = read
      if (shownRequestId.current === requestId) setRequest(read)

      if (read.status !== 'Requested') return read
    }

    if (shownRequestId.current === requestId) setFollowTimedOut(true)
    return latest
  }, [])

  const mutation = useMutation({
    mutationFn: ({ job, parameters }: { job: EtlJob; parameters: Record<string, string> }) =>
      // Empty values are omitted rather than sent blank: the server treats an absent optional parameter
      // as "not supplied", and a blank string as a value to validate.
      triggerEtlJob(
        job.jobId,
        Object.fromEntries(Object.entries(parameters).filter(([, v]) => v.trim() !== ''))
      ),
    onSuccess: async (accepted, { job }) => {
      shownRequestId.current = accepted.requestId
      setRequest(accepted)
      setFollowTimedOut(false)
      queryClient.invalidateQueries({ queryKey: ['etl-job-trigger-requests'] })

      notifications.show({
        title: 'Run requested',
        message: `${job.displayName} is recorded. Automaton picks requests up within seconds while it is running.`,
        color: 'blue',
      })

      const resolved = await followUntilResolved(accepted.requestId)
      queryClient.invalidateQueries({ queryKey: ['etl-job-trigger-requests'] })

      const { title, message, color } = followUpToast(job.displayName, resolved)
      notifications.show({ title, message, color })
    },
    onError: (error: Error) => {
      // Not sent (no Entra token here) or refused with a 401: the run endpoint is pinned to an Entra sign-in and
      // refuses the shared API key even locally, so say that rather than "Unauthorized". Otherwise the API's
      // message says which parameter was wrong, or why the run was refused. Show it.
      notifications.show({
        title: 'Could not run',
        message: signInRequiredText(error, entraConfigured) ?? error.message,
        color: 'red',
      })
    },
  })

  return {
    trigger: mutation.mutate,
    isPending: mutation.isPending,
    error: mutation.error,
    request,
    followTimedOut,
    reset: () => {
      shownRequestId.current = null
      setRequest(null)
      setFollowTimedOut(false)
      mutation.reset()
    },
  }
}
